import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../lib/routes';
import { useTranslation } from '../../hooks/useTranslation';
import LanguageSelector from './LanguageSelector';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import AuthModal from '../auth/AuthModal';

const { FiSearch, FiMenu, FiX, FiUser, FiLogOut, FiSettings, FiLayout } = FiIcons;

export default function Header() {
  const { t } = useTranslation();
  const { user, profile, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const userRoles =
    profile?.hahub_profile_roles
      ?.map((r) => r?.hahub_roles?.name)
      .filter(Boolean) || [];

  const isAdmin = userRoles.includes('super_admin') || userRoles.includes('moderator');
  const isVolunteer = userRoles.includes('volunteer') || isAdmin;

  const navLinks = [
    { name: t('nav.resources'), path: ROUTES.PUBLIC.RESOURCES },
    { name: t('nav.caregiverHub'), path: ROUTES.PUBLIC.CAREGIVER_HUB },
    { name: t('nav.directory'), path: ROUTES.PUBLIC.DIRECTORY },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to={ROUTES.PUBLIC.HOME} className="flex items-center gap-2">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-200">
              <span className="text-xl font-bold">H</span>
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent hidden sm:block">
              HAHUB
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to={ROUTES.PUBLIC.SEARCH}
              className="p-2 text-slate-500 hover:bg-slate-100 rounded-full transition-colors"
            >
              <SafeIcon icon={FiSearch} className="w-5 h-5" />
            </Link>

            <div className="hidden sm:block h-6 w-px bg-slate-200 mx-1" />
            <LanguageSelector />
            <div className="hidden sm:block h-6 w-px bg-slate-200 mx-1" />

            {user ? (
              <div className="flex items-center gap-3">
                {isVolunteer && (
                  <Link
                    to={ROUTES.VOLUNTEER.DASHBOARD}
                    className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-colors"
                  >
                    <SafeIcon icon={FiLayout} className="w-4 h-4" />
                    <span>Dashboard</span>
                  </Link>
                )}

                {isAdmin && (
                  <Link
                    to={ROUTES.ADMIN.DASHBOARD}
                    className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-semibold hover:bg-indigo-100 transition-colors"
                  >
                    <SafeIcon icon={FiSettings} className="w-4 h-4" />
                    <span>Admin</span>
                  </Link>
                )}

                <button
                  onClick={() => signOut()}
                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                  title="Sign Out"
                >
                  <SafeIcon icon={FiLogOut} className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-all shadow-md shadow-indigo-100"
              >
                <SafeIcon icon={FiUser} className="w-4 h-4" />
                <span>Sign In</span>
              </button>
            )}

            <button
              className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 shadow-xl animate-in slide-in-from-top-4 duration-200">
          <nav className="flex flex-col p-4 gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="px-4 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-xl transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}

            {isVolunteer && (
              <Link
                to={ROUTES.VOLUNTEER.DASHBOARD}
                className="px-4 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-xl flex items-center gap-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <SafeIcon icon={FiLayout} className="w-5 h-5" />
                Dashboard
              </Link>
            )}

            {isAdmin && (
              <Link
                to={ROUTES.ADMIN.DASHBOARD}
                className="px-4 py-3 text-base font-semibold text-indigo-600 bg-indigo-50 rounded-xl flex items-center gap-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <SafeIcon icon={FiSettings} className="w-5 h-5" />
                Admin Dashboard
              </Link>
            )}
          </nav>
        </div>
      )}

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </header>
  );
}